import React, { useEffect, useState } from "react";
import { api, ApiClientError } from "../api";
import type { DashboardMvpB, Opportunity } from "../types";
import { useNavigate } from "../router";
import { useToast } from "../context/ToastContext";

export default function Dashboard() {
  const navigate = useNavigate();
  const { show } = useToast();
  const [breaches, setBreaches] = useState<Opportunity[]>([]);
  const [metrics, setMetrics] = useState<DashboardMvpB | null>(null);
  const [loadingMetrics, setLoadingMetrics] = useState(false);

  useEffect(() => {
    api.slaBreaches().then(setBreaches).catch(() => setBreaches([]));
    setLoadingMetrics(true);
    api
      .getDashboardMvpB()
      .then(setMetrics)
      .catch((e) => {
        if (e instanceof ApiClientError) show(e.message, "error");
      })
      .finally(() => setLoadingMetrics(false));
  }, [show]);

  // Every metric carries a `drilldown` API path from the backend, per the
  // contract's "no aggregate-only KPI" rule. This frontend maps those API
  // paths onto the closest existing screen instead of a dead-end number:
  // conversation-shaped drilldowns go to the Inbox, everything else (mostly
  // `/api/opportunities`) goes to the Kanban board.
  function goToDrilldown(drilldown?: string) {
    if (!drilldown) return;
    if (drilldown.includes("/conversations")) navigate("/inbox");
    else navigate("/kanban");
  }

  return (
    <div className="dashboard-page">
      <h1>Dashboard</h1>
      <p className="meta">
        Métricas del embudo IA + comercial (MVP-B) y la cola de SLA vencido para gestión inmediata. Los números son
        clickeables y llevan al detalle correspondiente.
      </p>

      {loadingMetrics && <div className="loading-strip">Cargando métricas...</div>}

      {metrics && (
        <>
          <div className="dashboard-section">
            <h2>Embudo IA</h2>
            <div className="dashboard-metrics">
              <button className="metric-tile" onClick={() => goToDrilldown(metrics.funnel_ia.leads_totales.drilldown)}>
                <div className="label">Leads totales</div>
                <div className="value">{metrics.funnel_ia.leads_totales.value}</div>
              </button>
              <button className="metric-tile" onClick={() => goToDrilldown(metrics.funnel_ia.respondidos_por_ia.drilldown)}>
                <div className="label">Respondidos por IA</div>
                <div className="value">{metrics.funnel_ia.respondidos_por_ia.value}</div>
              </button>
              <button className="metric-tile" onClick={() => goToDrilldown(metrics.funnel_ia.handoffs_a_humano.drilldown)}>
                <div className="label">Handoffs a humano</div>
                <div className="value">{metrics.funnel_ia.handoffs_a_humano.value}</div>
              </button>
            </div>
            {metrics.funnel_ia.motivo_handoff.length > 0 && (
              <p className="meta" style={{ marginTop: 8 }}>
                Motivos de handoff:{" "}
                {metrics.funnel_ia.motivo_handoff.map((m) => `${m.reason_code} (${m.count})`).join(" · ")}
              </p>
            )}
          </div>

          <div className="dashboard-section">
            <h2>Comercial</h2>
            <div className="dashboard-metrics">
              <button className="metric-tile" onClick={() => goToDrilldown(metrics.comercial.cierres.drilldown)}>
                <div className="label">Cierres</div>
                <div className="value">{metrics.comercial.cierres.value}</div>
              </button>
              <button className="metric-tile" onClick={() => navigate("/kanban?pipeline=appointment")}>
                <div className="label">Citas propuestas</div>
                <div className="value">{metrics.comercial.citas_propuestas}</div>
              </button>
              <button className="metric-tile" onClick={() => navigate("/kanban?pipeline=appointment")}>
                <div className="label">Citas señadas</div>
                <div className="value">{metrics.comercial.citas_senadas}</div>
              </button>
              <button className="metric-tile" onClick={() => navigate("/kanban?pipeline=appointment")}>
                <div className="label">Citas confirmadas</div>
                <div className="value">{metrics.comercial.citas_confirmadas}</div>
              </button>
              <button className="metric-tile" onClick={() => navigate("/kanban?pipeline=appointment")}>
                <div className="label">Citas asistidas</div>
                <div className="value">{metrics.comercial.citas_asistidas}</div>
              </button>
            </div>
          </div>

          <div className="dashboard-section">
            <h2>Experiencia</h2>
            <div className="dashboard-metrics">
              <div className="metric-tile" style={{ cursor: "default" }}>
                <div className="label">Solicitudes de reseña</div>
                <div className="value">{metrics.experiencia.solicitudes_resena}</div>
              </div>
              <div className="metric-tile" style={{ cursor: "default" }}>
                <div className="label">Reseñas obtenidas</div>
                <div className="value">{metrics.experiencia.resenas_obtenidas}</div>
              </div>
              <div className="metric-tile" style={{ cursor: "default" }}>
                <div className="label">Satisfacción promedio</div>
                <div className="value">{metrics.experiencia.satisfaccion_promedio ?? "-"}</div>
              </div>
            </div>
          </div>
        </>
      )}

      <div className="dashboard-section">
        <h2>SLA vencido</h2>
        <div className="kpi" style={{ marginTop: 8 }}>
          <div>
            Oportunidades con SLA vencido <b className="overdue">{breaches.length}</b>
          </div>
        </div>
        <table className="task-table" style={{ marginTop: 16 }}>
          <thead>
            <tr>
              <th>Contacto</th>
              <th>Etapa</th>
              <th>Responsable</th>
              <th>Minutos vencido</th>
            </tr>
          </thead>
          <tbody>
            {breaches.map((o) => (
              <tr key={o.id}>
                <td>{o.contact_name}</td>
                <td>{o.stage_name}</td>
                <td>{o.owner?.name || "Sin asignar"}</td>
                <td className="overdue">{o.sla?.minutes_idle}</td>
              </tr>
            ))}
            {breaches.length === 0 && (
              <tr>
                <td colSpan={4} className="meta">
                  Sin incumplimientos de SLA activos.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
